'use client'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import {
    SystemUpdateAlt as UpdateIcon,
    CheckCircle as CheckCircleIcon,
    Download as DownloadIcon,
    RestartAlt as RestartIcon,
    Refresh as RefreshIcon,
    Info as InfoIcon,
    FolderOpen as FolderOpenIcon,
    Warning as WarningIcon,
    UploadFile as UploadFileIcon,
} from '@mui/icons-material'
import Tooltip from '@mui/material/Tooltip'
import { useUpdate } from '@/contexts/UpdateContext'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 0 60px 0;
    animation: ${fadeIn} 0.35s ease-out;
`

const PageHeader = styled.div`
    margin-bottom: 40px;
    flex-direction: column;
`

const PageTitle = styled.h1`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 2rem;
    font-weight: 800;
    color: white;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
    text-transform: uppercase;

    span {
        color: #6366f1;
        text-shadow: 0 0 20px rgba(99, 102, 241, 0.5);
    }
`

const PageSubtitle = styled.p`
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.4);
    margin: 0;
    letter-spacing: 0.5px;
`

const Section = styled.div`
    margin-bottom: 32px;
    flex-direction: column;
    width: 100%;
`

const SectionTitle = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    width: 100%;
`

const SettingCard = styled.div`
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid ${({ $variant }) =>
        $variant === 'warning' ? 'rgba(251, 191, 36, 0.15)'
        : $variant === 'success' ? 'rgba(74, 222, 128, 0.12)'
        : 'rgba(255, 255, 255, 0.06)'};
    border-radius: 14px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: border-color 0.2s ease;
    width: 100%;

    &:hover {
        border-color: ${({ $variant }) =>
            $variant === 'warning' ? 'rgba(251, 191, 36, 0.3)'
            : $variant === 'success' ? 'rgba(74, 222, 128, 0.25)'
            : 'rgba(99, 102, 241, 0.2)'};
    }
`

const CardIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ $variant }) =>
        $variant === 'warning' ? 'rgba(251, 191, 36, 0.1)'
        : $variant === 'success' ? 'rgba(74, 222, 128, 0.1)'
        : 'rgba(99, 102, 241, 0.1)'};
    border: 1px solid ${({ $variant }) =>
        $variant === 'warning' ? 'rgba(251, 191, 36, 0.25)'
        : $variant === 'success' ? 'rgba(74, 222, 128, 0.2)'
        : 'rgba(99, 102, 241, 0.2)'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${({ $variant }) =>
        $variant === 'warning' ? '#fbbf24'
        : $variant === 'success' ? '#4ade80'
        : '#818cf8'};
`

const CardContent = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const CardTitle = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 0.3px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`

const CardDesc = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.4;
`

const PathMonoText = styled.span`
    font-family: 'JetBrains Mono', 'Consolas', monospace;
    font-size: 11px;
    color: rgba(74, 222, 128, 0.8);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    max-width: 420px;
`

const CardActions = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`

const ActionBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 8px 16px;
    border-radius: 8px;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    border: 1px solid ${({ $variant }) =>
        $variant === 'success' ? 'rgba(34, 197, 94, 0.4)'
        : $variant === 'download' ? 'rgba(99, 102, 241, 0.5)'
        : 'rgba(255, 255, 255, 0.15)'};
    background: ${({ $variant }) =>
        $variant === 'success' ? 'rgba(34, 197, 94, 0.1)'
        : $variant === 'download' ? 'rgba(99, 102, 241, 0.15)'
        : 'rgba(255, 255, 255, 0.04)'};
    color: ${({ $variant }) =>
        $variant === 'success' ? '#86efac'
        : $variant === 'download' ? '#a5b4fc'
        : 'rgba(255, 255, 255, 0.7)'};

    &:hover:not(:disabled) {
        filter: brightness(1.2);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    svg {
        font-size: 15px;
    }
`

const SmallIconBtn = styled.button`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`

const StatusBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: ${({ $color }) => $color || 'rgba(255,255,255,0.5)'};
    padding: 6px 12px;
    border-radius: 6px;
    background: ${({ $bg }) => $bg || 'transparent'};
`

const ProgressTrack = styled.div`
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.07);
    border-radius: 2px;
    overflow: hidden;
    margin-top: 8px;
`

const ProgressFill = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #6366f1, #a855f7);
    border-radius: 2px;
    transition: width 0.4s ease;
    width: ${({ $pct }) => $pct}%;
`

const SpinWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    animation: ${spin} 1s linear infinite;
`

const VersionTag = styled.span`
    font-family: 'Consolas', monospace;
    font-size: 11px;
    background: rgba(99, 102, 241, 0.15);
    color: #818cf8;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid rgba(99, 102, 241, 0.2);
`

const APP_VERSION = '1.0.5'

// --- Update Card ---
const UpdateCardContent = () => {
    const { phase, version, progress, checkForUpdate, download, install } = useUpdate()

    const renderStatus = () => {
        switch (phase) {
            case 'checking':
                return (
                    <StatusBadge $color="rgba(255,255,255,0.5)">
                        <SpinWrapper><RefreshIcon style={{ fontSize: 14 }} /></SpinWrapper>
                        Verificando...
                    </StatusBadge>
                )
            case 'up-to-date':
                return (
                    <StatusBadge $color="#86efac" $bg="rgba(34,197,94,0.08)">
                        <CheckCircleIcon style={{ fontSize: 14 }} />
                        Estás actualizado
                    </StatusBadge>
                )
            case 'dev':
                return (
                    <StatusBadge $color="rgba(255,171,0,0.8)" $bg="rgba(255,171,0,0.07)">
                        <InfoIcon style={{ fontSize: 14 }} />
                        Modo desarrollo
                    </StatusBadge>
                )
            case 'available':
                return (
                    <ActionBtn $variant="download" onClick={download}>
                        <DownloadIcon />
                        Descargar {version}
                    </ActionBtn>
                )
            case 'downloading':
                return (
                    <StatusBadge $color="#a5b4fc">
                        <DownloadIcon style={{ fontSize: 14 }} />
                        {progress}%
                    </StatusBadge>
                )
            case 'ready':
                return (
                    <ActionBtn $variant="success" onClick={install}>
                        <RestartIcon />
                        Reiniciar e instalar
                    </ActionBtn>
                )
            default:
                return (
                    <ActionBtn onClick={checkForUpdate}>
                        <RefreshIcon />
                        Buscar actualización
                    </ActionBtn>
                )
        }
    }

    return (
        <SettingCard>
            <CardIcon>
                <UpdateIcon style={{ fontSize: 20 }} />
            </CardIcon>
            <CardContent>
                <CardTitle>
                    Actualizaciones automáticas
                    <VersionTag>v{APP_VERSION}</VersionTag>
                </CardTitle>
                <CardDesc>
                    {phase === 'available'
                        ? `Nueva versión ${version} disponible`
                        : phase === 'downloading'
                        ? 'Descargando actualización...'
                        : phase === 'ready'
                        ? 'Actualización lista — reiniciá para aplicar'
                        : 'Verificá si hay una nueva versión de ConfigStrike'}
                </CardDesc>
                {phase === 'downloading' && (
                    <ProgressTrack>
                        <ProgressFill $pct={progress} />
                    </ProgressTrack>
                )}
            </CardContent>
            <CardActions>
                {renderStatus()}
            </CardActions>
        </SettingCard>
    )
}

// --- CS2 Path Card ---
const CS2PathCard = () => {
    const { cs2Path, isDetecting, handleDetectPath, handleManualPathSelect } = useConfig()
    const { t } = useLanguage()
    const found = !!cs2Path

    return (
        <SettingCard $variant={found ? 'success' : 'warning'}>
            <CardIcon $variant={found ? 'success' : 'warning'}>
                {found
                    ? <FolderOpenIcon style={{ fontSize: 20 }} />
                    : <WarningIcon style={{ fontSize: 20 }} />}
            </CardIcon>
            <CardContent>
                <CardTitle>Carpeta de CS2</CardTitle>
                <CardDesc>
                    {found
                        ? <PathMonoText>{cs2Path.length > 65 ? '...' + cs2Path.slice(-65) : cs2Path}</PathMonoText>
                        : (t('CS2_FOLDER_NOT_FOUND_DESC') || 'No se detectó la carpeta de configuración de CS2')}
                </CardDesc>
            </CardContent>
            <CardActions>
                <ActionBtn onClick={handleManualPathSelect}>
                    <UploadFileIcon />
                    Seleccionar
                </ActionBtn>
                <Tooltip title={t('RECALCULATE_PATH') || 'Detectar automáticamente'} placement="top">
                    <SmallIconBtn onClick={handleDetectPath} disabled={isDetecting}>
                        {isDetecting
                            ? <SpinWrapper><RefreshIcon style={{ fontSize: 16 }} /></SpinWrapper>
                            : <RefreshIcon style={{ fontSize: 16 }} />}
                    </SmallIconBtn>
                </Tooltip>
            </CardActions>
        </SettingCard>
    )
}

const SettingsView = () => {
    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    <span>Confi</span>guración
                </PageTitle>
                <PageSubtitle>Ajustes de la aplicación</PageSubtitle>
            </PageHeader>

            <Section>
                <SectionTitle>Juego</SectionTitle>
                <CS2PathCard />
            </Section>

            <Section>
                <SectionTitle>Aplicación</SectionTitle>
                <UpdateCardContent />
            </Section>
        </Container>
    )
}

export default SettingsView
